import React, { useState, useEffect, useCallback } from 'react';
import { MdAdd, MdClose } from 'react-icons/md';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { inviteTeamMembers, fetchTeamMembers, removeTeamMember, TeamMember } from 'services/apiService';

const isAdmin = localStorage.getItem('isOrganizationAdmin') === 'true';

interface InviteRow {
  name: string;
  email: string;
}

const emptyRow = (): InviteRow => ({ name: '', email: '' });

const InviteTeamForm = () => {
  const [rows, setRows] = useState<InviteRow[]>([emptyRow()]);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const data = await fetchTeamMembers();
      setMembers(data);
    } catch {
      // silently fail
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const updateRow = (index: number, field: keyof InviteRow, value: string) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const removeRow = (index: number) => {
    if (rows.length === 1) return;
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = rows.filter(r => r.email.trim());
    if (valid.length === 0) return;
    setLoading(true);
    try {
      const data = await inviteTeamMembers(valid.map(r => ({ email: r.email.trim(), name: r.name.trim() })));
      toast.success(data.message);
      setRows([emptyRow()]);
    } catch {
      toast.error('Error sending invites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (member: TeamMember) => {
    if (!window.confirm(`Remove ${member.email} from your organisation?`)) return;
    setRemovingId(member.id);
    try {
      await removeTeamMember(member.id);
      toast.success(`${member.email} removed.`);
      setMembers(prev => prev.filter(m => m.id !== member.id));
    } catch {
      toast.error('Could not remove member. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-sm p-6">
      {/* Team Members */}
      <h4 className="text-base font-semibold text-[#1F2937] dark:text-white mb-4">
        Team Members
      </h4>

      {membersLoading ? (
        <p className="text-sm text-gray-500">Loading members…</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-gray-500">No members yet.</p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-navy-600">
          {members.map(member => (
            <div key={member.id} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center text-brand-600 dark:text-brand-300 font-semibold text-sm">
                  {(member.full_name || member.email).charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1F2937] dark:text-white flex items-center gap-2">
                    {member.full_name || member.email}
                    {member.is_organization_admin && (
                      <span className="rounded-full bg-brand-100 dark:bg-brand-900 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:text-brand-300">
                        Admin
                      </span>
                    )}
                  </p>
                  {member.full_name && (
                    <p className="text-xs text-[#6B7280]">{member.email}</p>
                  )}
                </div>
              </div>
              {isAdmin && !member.is_organization_admin && (
                <button
                  onClick={() => handleRemove(member)}
                  disabled={removingId === member.id}
                  className="text-xs font-semibold text-red-500 hover:text-red-700 disabled:opacity-40 transition-colors"
                >
                  {removingId === member.id ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Invite form — admin only */}
      {isAdmin && (
        <div className="border-t border-gray-100 dark:border-navy-600 mt-6 pt-6">
          <h4 className="text-base font-semibold text-[#1F2937] dark:text-white mb-1">
            Invite Team Members
          </h4>
          <p className="text-xs text-[#6B7280] dark:text-gray-300 mb-4">
            Enter a name and email for each person you'd like to invite.
          </p>

          <form onSubmit={handleInvite} className="flex flex-col gap-3">
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-1">
              <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Name</span>
              <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Email</span>
              <span className="w-8" />
            </div>

            {/* Invite rows */}
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                <input
                  type="text"
                  value={row.name}
                  onChange={e => updateRow(i, 'name', e.target.value)}
                  placeholder="Full name"
                  className="border border-gray-200 dark:border-navy-600 rounded-lg px-3 py-2 bg-white dark:bg-navy-700 text-sm text-[#1F2937] dark:text-white placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <input
                  type="email"
                  value={row.email}
                  onChange={e => updateRow(i, 'email', e.target.value)}
                  placeholder="email@company.com"
                  required={i === 0}
                  className="border border-gray-200 dark:border-navy-600 rounded-lg px-3 py-2 bg-white dark:bg-navy-700 text-sm text-[#1F2937] dark:text-white placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  disabled={rows.length === 1}
                  className="h-8 w-8 flex items-center justify-center rounded-lg text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 disabled:opacity-30 transition-colors"
                  aria-label="Remove row"
                >
                  <MdClose className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Add another */}
            <button
              type="button"
              onClick={addRow}
              className="flex items-center gap-1.5 text-sm font-medium text-brand-500 hover:text-brand-600 w-fit transition-colors"
            >
              <MdAdd className="h-4 w-4" />
              Add another
            </button>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-60 py-2.5 text-sm font-semibold text-white transition-colors mt-1"
            >
              {loading ? 'Sending…' : 'Send Invites'}
            </button>
          </form>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default InviteTeamForm;
